import { AddOutlined, DeleteOutline } from "@mui/icons-material";
import { Box, Button, Card, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";
import { Control, Controller, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  IVersionAction,
  IVersionItem,
} from "../../../shared/interfaces/version/IVersion";

interface IProps {
  control: Control<IVersionAction>;
  isUpdate?: boolean;
}

const VersionForm = ({ control, isUpdate = false }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fields, append, remove } = useFieldArray({ control, name: "items" });

  return (
    <Card>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Controller
            name={"version"}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                error={!!error?.message}
                helperText={error?.message ? t(error?.message) : ""}
                value={value ?? ""}
                onChange={onChange}
                label={t("version.name")}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("general.isActive")}
                select
                SelectProps={{ native: true }}
                fullWidth
              >
                <option value="true">{t("general.true")}</option>
                <option value="false">{t("general.false")}</option>
              </TextField>
            )}
          />
        </Grid>
        {fields?.map((_, index) => (
          <React.Fragment key={index}>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Controller
                name={`items.${index}.title`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label={t("version.itemTitle")}
                    error={!!error}
                    helperText={error?.message ? t(error.message) : ""}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Controller
                name={`items.${index}.text`}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    label={t("general.description")}
                    value={value ?? ""}
                    onChange={onChange}
                    error={!!error?.message}
                    helperText={t(error?.message as string)}
                    multiline
                    minRows={3}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 3 }}>
              <Button
                color="error"
                variant={"contained"}
                onClick={() => remove(index)}
                startIcon={<DeleteOutline />}
              >
                {t("button.removeItem")}
              </Button>
            </Grid>
          </React.Fragment>
        ))}
        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
          <Button
            variant="contained"
            onClick={() => append({ title: "", text: "" } as IVersionItem)}
            startIcon={<AddOutlined />}
          >
            {t("button.addItem")}
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant={"contained"}
          color={"secondary"}
          sx={{ marginTop: "20px" }}
          type={"button"}
          onClick={() => navigate(-1)}
        >
          {t("button.cancel")}
        </Button>
        <Button
          variant={"contained"}
          sx={{ margin: "20px 0 0 10px" }}
          type={"submit"}
        >
          {t(isUpdate ? "button.update" : "button.create")}
        </Button>
      </Box>
    </Card>
  );
};

export default VersionForm;
